import React, { useRef, useEffect, useState } from "react";
import { view } from "@risingstack/react-easy-state";
import "./ChatWidget.css";

const formatTimestamp = (timestamp) => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (error) {
    console.error("Error formatting timestamp:", error);
    return timestamp;
  }
};

const ChatWidget = view(
  ({
    messages = { broadcast: "", master: "", unreadCount: 0, isExpanded: false },
    onSendMessage,
  }) => {
    const [messageInput, setMessageInput] = React.useState("");
    const [isExpanded, setIsExpanded] = useState(false);
    const chatViewerRef = useRef(null);
    const prevMessagesRef = useRef([]);

    // Add logging for messages prop changes
    useEffect(() => {
      console.log("[ChatWidget] Messages state:", messages);
      console.log("[ChatWidget] Unread count:", messages.unreadCount);
      console.log("[ChatWidget] Widget expanded:", isExpanded);
    }, [messages, messages.unreadCount, isExpanded]);

    useEffect(() => {
      if (chatViewerRef.current) {
        chatViewerRef.current.scrollTop = chatViewerRef.current.scrollHeight;
      }
    }, [messages]);

    // Track new messages and update unread count
    useEffect(() => {
      // Combine both broadcast and private messages
      const allMessages = [
        ...(messages.broadcast || "")
          .split("\n")
          .map((msg) => ({ msg, type: "broadcast" })),
        ...(messages.master || "")
          .split("\n")
          .map((msg) => ({ msg, type: "private" })),
      ]
        .filter(({ msg }) => msg.trim())
        .sort((a, b) => {
          const timeA = a.msg.split(" - ")[0];
          const timeB = b.msg.split(" - ")[0];
          return new Date(timeA) - new Date(timeB);
        });

      const currentMessages = allMessages
        .map(({ msg, type }) => ({ ...parseMessage(msg), type }))
        .filter((msg) => msg !== null);

      // Update unread count when widget is not expanded
      if (!isExpanded) {
        // Count all received messages (not sent by current user)
        const receivedMessages = currentMessages.filter(
          (msg) => !msg.isSentMessage
        );
        const prevReceivedMessages = prevMessagesRef.current.filter(
          (msg) => !msg.isSentMessage
        );

        if (receivedMessages.length > prevReceivedMessages.length) {
          const newCount =
            receivedMessages.length - prevReceivedMessages.length;
          console.log("New messages received:", newCount); // Debug log
          messages.unreadCount = newCount;
        }
      }

      prevMessagesRef.current = currentMessages;
    }, [messages, isExpanded]);

    const handleSubmit = (e) => {
      e.preventDefault();
      if (messageInput.trim()) {
        onSendMessage(messageInput);
        setMessageInput("");
      }
    };

    const toggleWidget = () => {
      console.log("[ChatWidget] Toggling widget. Current state:", isExpanded);
      setIsExpanded(!isExpanded);
      if (!isExpanded) {
        console.log("[ChatWidget] Opening widget, clearing unread count");
        messages.unreadCount = 0;
      }
      messages.isExpanded = !isExpanded;
      console.log("[ChatWidget] New widget state:", !isExpanded);
      console.log("[ChatWidget] Updated unread count:", messages.unreadCount);
    };

    const parseMessage = (messageString) => {
      try {
        const [timestamp, ...rest] = messageString.split(" - ");
        if (rest.length >= 1) {
          const senderAndContent = rest.join(" - ");
          const [sender, ...contentParts] = senderAndContent.split(": ");
          const content = contentParts.join(": ").trim();
          return {
            timestamp,
            sender: sender.trim(),
            content,
            isSentMessage: sender.trim() === "You",
          };
        }
        return null;
      } catch (error) {
        console.error("Error parsing message:", error);
        return null;
      }
    };

    // Combine and sort all messages
    const allMessages = [
      ...(messages.broadcast || "")
        .split("\n")
        .map((msg) => ({ msg, type: "broadcast" })),
      ...(messages.master || "")
        .split("\n")
        .map((msg) => ({ msg, type: "private" })),
    ]
      .filter(({ msg }) => msg.trim())
      .sort((a, b) => {
        const timeA = a.msg.split(" - ")[0];
        const timeB = b.msg.split(" - ")[0];
        return new Date(timeA) - new Date(timeB);
      })
      .map(({ msg, type }) => ({ ...parseMessage(msg), type }))
      .filter((msg) => msg !== null);

    return (
      <div className={`chat-widget-container ${isExpanded ? "expanded" : ""}`}>
        {!isExpanded ? (
          <button className="chat-toggle-button" onClick={toggleWidget}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {messages.unreadCount > 0 && (
              <span className="notification-badge">
                {console.log(
                  "[ChatWidget] Rendering badge with count:",
                  messages.unreadCount
                )}
                {messages.unreadCount}
              </span>
            )}
          </button>
        ) : (
          <div className="chat-widget">
            <div className="chat-header">
              <h3>Messages</h3>
              <button
                className="minimize-button"
                onClick={toggleWidget}
                title="Close chat"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div className="chat-messages" ref={chatViewerRef}>
              {allMessages.map((message, index) => (
                <div
                  key={index}
                  className={`message ${
                    message.isSentMessage ? "sent-message" : "received-message"
                  }`}
                >
                  <div className="message-bubble">
                    <div className="message-content">
                      {message.content}
                      <span className={`message-type ${message.type}`}>
                        {message.type}
                      </span>
                    </div>
                    <span className="message-time">
                      {formatTimestamp(message.timestamp)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <form className="chat-input" onSubmit={handleSubmit}>
              <div className="input-wrapper">
                <input
                  type="text"
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder="Type a message..."
                  className="message-input"
                />
                <button
                  type="submit"
                  className="send-button"
                  title="Send message"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M22 2L11 13"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M22 2L15 22L11 13L2 9L22 2Z"
                      fill="currentColor"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    );
  }
);

export default ChatWidget;
